<template>
  <TransitionRoot as="template" :show="quickApplySuccessOpen">
    <Dialog as="div" class="fixed z-50 inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @close="emit('close')">
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex mx-auto min-h-full md:w-[520px] md:max-w-max items-end justify-center p-4 text-center sm:items-center">
          <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
                class="relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all md:w-[80rem]"
            >
              <Icon
                  name="close"
                  class="absolute right-4 top-4 cursor-pointer"
                  @click="closeSuccessModal"
              />
              <div class="flex flex-col items-center bg-white px-12 text-center shadow sm:rounded-lg">
                <VerifyYourAccountModal :modalData="modalData" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <div class="flex flex-wrap gap-x-8">
    <div
      class="transition-all duration-300 ease-in-out"
      :class="
        detailPage && (aiAnalyze || loadingAiAnalyze)
          ? 'w-full sm:w-[60%]'
          : 'w-full'
      "
    >
      <div class="mt-4 rounded-app bg-white p-4 shadow-md md:p-8">
        <!-- TOP -->
        <div class="flex gap-4">
          <div class="flex items-center text-sm">
            <Icon name="calendar_month" class="mr-2 text-primary" />
            <div v-if="job.job_reactivated && job.show_job_reactivated" class="flex items-center gap-2 text-body">
              <span v-if="job.isRecruiter">{{ __('Posted') }} {{ job.posted_at }}</span>
              <span :class="{ 'border-l border-black pl-2': job.isRecruiter }">{{ __('Updated') }} {{ job.reactivated_at }}</span>
            </div>
            <div v-else>
              <span>{{ __('Posted') }} {{ job.posted_at }}</span>
            </div>
          </div>

          <div class="flex items-center text-sm">
            <Icon name="visibility" class="mr-2 text-primary" />
            <span class="text-body"
              >{{ job.job_views_count }} {{ __('views') }}</span
            >
          </div>

          <div class="hidden items-center text-sm md:flex">
            <Icon name="share" class="mr-2 text-primary" />
            <span class="text-body"> {{ __('Share this job') }}</span>
            <div class="ml-4 flex gap-2">
              <a
                :href="`https://www.linkedin.com/sharing/share-offsite/?url=${job.url}`"
                target="_blank"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 1.4325C0 0.64125 0.6575 0 1.46875 0H18.5312C19.3425 0 20 0.64125 20 1.4325V18.5675C20 19.3587 19.3425 20 18.5312 20H1.46875C0.6575 20 0 19.3587 0 18.5675V1.4325ZM6.17875 16.7425V7.71125H3.1775V16.7425H6.17875ZM4.67875 6.4775C5.725 6.4775 6.37625 5.785 6.37625 4.9175C6.3575 4.03125 5.72625 3.3575 4.69875 3.3575C3.67125 3.3575 3 4.0325 3 4.9175C3 5.785 3.65125 6.4775 4.65875 6.4775H4.67875ZM10.8137 16.7425V11.6988C10.8137 11.4288 10.8337 11.1587 10.9137 10.9662C11.13 10.4275 11.6238 9.86875 12.4538 9.86875C13.54 9.86875 13.9738 10.6963 13.9738 11.9113V16.7425H16.975V11.5625C16.975 8.7875 15.495 7.4975 13.52 7.4975C11.9275 7.4975 11.2137 8.3725 10.8137 8.98875V9.02H10.7937C10.8004 9.00957 10.8071 8.99915 10.8137 8.98875V7.71125H7.81375C7.85125 8.55875 7.81375 16.7425 7.81375 16.7425H10.8137Z"
                    fill="#52525B"
                  />
                </svg>
              </a>
              <a
                :href="`https://www.facebook.com/sharer/sharer.php?u=${job.url}`"
                target="_blank"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.9 0H1.1C0.808262 0 0.528473 0.115893 0.322183 0.322183C0.115893 0.528473 0 0.808262 0 1.1V18.9C0 19.1917 0.115893 19.4715 0.322183 19.6778C0.528473 19.8841 0.808262 20 1.1 20H10.68V12.25H8.08V9.25H10.68V7C10.6261 6.47176 10.6885 5.93813 10.8627 5.43654C11.0369 4.93495 11.3188 4.47755 11.6885 4.09641C12.0582 3.71528 12.5068 3.41964 13.0028 3.23024C13.4989 3.04083 14.0304 2.96225 14.56 3C15.3383 2.99521 16.1163 3.03528 16.89 3.12V5.82H15.3C14.04 5.82 13.8 6.42 13.8 7.29V9.22H16.8L16.41 12.22H13.8V20H18.9C19.0445 20 19.1875 19.9715 19.321 19.9163C19.4544 19.861 19.5757 19.78 19.6778 19.6778C19.78 19.5757 19.861 19.4544 19.9163 19.321C19.9715 19.1875 20 19.0445 20 18.9V1.1C20 0.955546 19.9715 0.812506 19.9163 0.679048C19.861 0.54559 19.78 0.424327 19.6778 0.322183C19.5757 0.220038 19.4544 0.139013 19.321 0.0837326C19.1875 0.0284524 19.0445 0 18.9 0Z"
                    fill="#52525B"
                  />
                </svg>
              </a>
            </div>
          </div>

          <div
            v-if="
              aiAnalyze === null &&
              !loadingAiAnalyze &&
              user_type === 'user' &&
              job?.show_ai_candidate_job_analysis
            "
            class="ml-auto flex cursor-pointer items-center gap-x-2"
            @click.stop="
              detailPage ? analyzeJobWithAI() : goToDetailPage(job.url)
            "
          >
            <svg
              class="rounded-md bg-[#F0EEFF] p-1 transition-all duration-200 ease-in-out hover:bg-[#E7F8F2]"
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
            >
              <mask
                id="mask0_8110_6549"
                style="mask-type: alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="24"
              >
                <rect width="24" height="24" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_8110_6549)">
                <path
                  d="M16.15 13.05L14 16.5C13.8166 16.7833 13.5625 16.9 13.2375 16.85C12.9125 16.8 12.7083 16.6083 12.625 16.275L11.925 13.475L5.09995 20.3C4.91662 20.4833 4.68745 20.5792 4.41245 20.5875C4.13745 20.5958 3.89995 20.5 3.69995 20.3C3.51662 20.1167 3.42495 19.8833 3.42495 19.6C3.42495 19.3167 3.51662 19.0833 3.69995 18.9L10.525 12.05L7.72495 11.35C7.39162 11.2667 7.19995 11.0625 7.14995 10.7375C7.09995 10.4125 7.21662 10.1583 7.49995 9.97499L10.95 7.84999L10.65 3.77499C10.6166 3.44166 10.75 3.19999 11.05 3.04999C11.35 2.89999 11.625 2.93333 11.875 3.14999L15 5.77499L18.775 4.24999C19.0916 4.11666 19.3666 4.16666 19.6 4.39999C19.8333 4.63333 19.8833 4.90833 19.75 5.22499L18.225 8.99999L20.85 12.1C21.0666 12.35 21.1 12.625 20.95 12.925C20.8 13.225 20.5583 13.3583 20.225 13.325L16.15 13.05ZM3.34995 6.34999C3.24995 6.24999 3.19995 6.13333 3.19995 5.99999C3.19995 5.86666 3.24995 5.74999 3.34995 5.64999L4.64995 4.34999C4.74995 4.24999 4.86662 4.19999 4.99995 4.19999C5.13328 4.19999 5.24995 4.24999 5.34995 4.34999L6.64995 5.64999C6.74995 5.74999 6.79995 5.86666 6.79995 5.99999C6.79995 6.13333 6.74995 6.24999 6.64995 6.34999L5.34995 7.64999C5.24995 7.74999 5.13328 7.79999 4.99995 7.79999C4.86662 7.79999 4.74995 7.74999 4.64995 7.64999L3.34995 6.34999ZM17.65 20.65L16.35 19.35C16.25 19.25 16.2 19.1333 16.2 19C16.2 18.8667 16.25 18.75 16.35 18.65L17.65 17.35C17.75 17.25 17.8666 17.2 18 17.2C18.1333 17.2 18.25 17.25 18.35 17.35L19.65 18.65C19.75 18.75 19.8 18.8667 19.8 19C19.8 19.1333 19.75 19.25 19.65 19.35L18.35 20.65C18.25 20.75 18.1333 20.8 18 20.8C17.8666 20.8 17.75 20.75 17.65 20.65Z"
                  fill="#27272A"
                />
              </g>
            </svg>
            <h2 class="text-sm font-medium">
              {{ __('Analyze with AI') }}
            </h2>
          </div>
        </div>
        <!-- /TOP -->

        <!-- HERO -->
        <div class="mt-8">
          <div class="md:flex md:justify-between">
            <div class="flex justify-between">
              <a
                :href="
                  route('redesign.company.view', { company: job.company.slug })
                "
                target="_BLANK"
              >
                <div
                  class="flex items-center gap-2"
                  v-if="!config_general_single_company"
                >
                  <img
                    :src="job.company.logo"
                    alt="Company logo"
                    class="h-12 w-12 rounded-full"
                  />
                  <div>
                    <h3 class="my-2 text-lg text-[#212429]">
                      {{ job.company.name }}
                    </h3>
                    <p class="text-sm text-body">
                      {{ job.company.domain }}
                    </p>
                  </div>
                </div>
              </a>
            </div>

            <div v-if="job.internal" class="mt-4 md:mt-0">
              <span class="flex items-center text-sm text-body">
                <Icon name="apartment" class="mr-2 !text-sm text-primary" />
                {{ __('Internal job') }}
              </span>
            </div>
          </div>

          <div class="mt-4">
            <h1 class="my-4 text-3xl font-medium text-[#212429]">
              <Icon
                name="work"
                :is_filled="true"
                class="text-primary"
                v-if="config_general_single_company"
              />
              {{ job.title }}
            </h1>
          </div>
          <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div
              v-if="
                !config_general_single_company &&
                job.company.isCompanyApproved &&
                job.company.isVettingOn
              "
              class="flex items-center gap-2"
            >
              <Icon name="verified_user" class="text-status-active" />
              <span class="text-body">
                {{ __('Verified Company') }}
              </span>
            </div>
            <div class="flex items-center gap-2" v-if="job.city">
              <Icon name="distance" class="text-primary" />
              <span
                v-for="(city, index) in job.city"
                :key="index"
                class="text-body"
              >
                {{ city.name }}
                <span v-if="index !== job.city.length - 1">, </span>
              </span>
            </div>
          </div>

          <div v-if="job.conditions.can_edit">
            <JobQuickEdit :job="job" :target="target" />
          </div>

          <div class="mt-8 items-center justify-between md:flex">
            <div>
              <div v-if="user_type === 'user'">
                <div v-if="job.conditions.is_matched">
                  <Button
                    :content="__('Matched')"
                    class="!border-success !bg-success"
                    type="button"
                    variation="fill"
                  />
                </div>
                <div v-if="job.conditions.is_applied">
                  <Button
                    :content="__('Applied')"
                    class="!border-success !bg-success"
                    type="button"
                    variation="fill"
                  />
                </div>
                <div v-else-if="job.conditions.is_invited">
                  <Button
                    :content="__('Accept invitation')"
                    type="button"
                    variation="fill"
                    @click="handleAcceptInvitation"
                    :disabled="loading_handle_invitation || !job.active"
                  />
                  <Button
                    :content="__('Decline')"
                    class="ml-2"
                    type="button"
                    variation="outline"
                    @click="handleDeclineInvitation"
                    :disabled="loading_handle_invitation || !job.active"
                  />
                  <p v-if="!job.active" class="mt-4 text-body">
                    {{
                      __(
                        'The job has expired and you can no longer apply for it.'
                      )
                    }}
                  </p>
                </div>
                <div v-else-if="job.conditions.can_apply">
                  <div v-if="job.conditions.is_cv_completed">
                    <div class="flex items-center gap-4">
                      <JobSafetyHover v-if="reportMisconductOn" />
                      <Button
                        :content="
                          loadingApply ? __('Applying...') :
                            job.conditions.can_reapply
                              ? __('Reapply')
                              : __('Apply now')
                          "
                        :class="
                          job.conditions.is_applied
                            ? 'border-success bg-success'
                            : ''
                        "
                        :disabled="loadingApply"
                        type="button"
                        variation="fill"
                        @click="handleApply"
                      />
                    </div>
                    <p v-if="job.conditions.can_reapply" class="text-sm text-gray-500 mt-2">
                      {{ __('Your previous application has expired, but you can apply again.') }}
                    </p>
                  </div>

                  <div v-else>
                    <Button
                      :content="__('Apply now')"
                      disabled
                      type="button"
                      variation="fill"
                    />
                    <p class="mt-4 text-body">
                      {{
                        __(
                          "You cannot apply because you don't have the profile completed."
                        )
                      }}
                      <a
                        :href="user_profile"
                        class="font-bold text-primary underline"
                        >{{ __('Go to profile') }}</a
                      >
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="user_type === 'none'"
                class="flex flex-wrap items-start gap-2"
              >
                <a href="/login" target="_BLANK">
                  <Button
                    :content="__('Sign in & Apply')"
                    type="button"
                    variation="fill"
                  />
                </a>
                <Button
                  v-if="!showApplyForm"
                  :content="__('Easy Apply')"
                  type="button"
                  @click="showApplyForm = !showApplyForm"
                />
              </div>
            </div>

            <div class="mt-4 flex flex-wrap items-center gap-4 md:mt-0 md:flex">
              <a
                :href="job.url"
                target="_BLANK"
                class="flex items-center text-sm text-primary"
                v-if="job.active && !detailPage"
              >
                <Icon name="open_in_new" class="mr-2" />
                <span class="font-medium">
                  {{ __('Go to job page') }}
                </span>
              </a>

              <span
                @click="handleSave"
                class="flex cursor-pointer items-center text-sm"
                role="button"
                v-if="user_type === 'user' && job.active"
              >
                <Icon
                  name="bookmark"
                  class="mr-2 text-primary"
                  :is_filled="job.conditions.is_saved"
                  fill_color="#10b981"
                />
                <span class="font-medium text-primary">
                  {{ job.conditions.is_saved ? __('Saved') : __('Save') }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <!-- HERO -->
      </div>

      <div
        v-if="show_reject_reason"
        class="mt-4 rounded-app bg-white p-8 shadow-md"
      >
        <h3 class="mb-2 text-lg font-semibold text-zinc-800">
          {{ __('Reason for rejecting this invitation') }}
        </h3>
        <p class="text-sm text-zinc-800">
          {{
            __(
              'Could you please tell us why you declined the offer? Your feedback will help in refining our screening process for your profile.'
            )
          }}
        </p>
        <form @submit.prevent="handleRejectReason()">
          <Textarea
            v-model="reject_reason"
            :value="reject_reason"
            :placeholder="__('Reason for rejecting this invitation')"
            class="mt-4"
          />
          <div class="mt-4">
            <Button
              :content="__('Send')"
              icon="send"
              variation="fill"
              class="mr-2"
            />
            <Button
              :content="__('Skip')"
              icon="cancel"
              class="text-[#52525B]"
              variation="outline"
              type="button"
              @click="() => handleSkipReason()"
            />
          </div>
        </form>
      </div>

      <div v-if="showApplyForm" class="my-8">
        <h5 class="mb-4 text-lg font-medium">
          {{ __('Unlock your job opportunities. Apply with your CV.') }}
        </h5>

        <ApplyWithoutAccountForm
          @apply-success="handleApplySuccess"
          @close="showApplyForm = !showApplyForm"
          :job="job"
          :options="options"
          :terms="terms"
          :privacy="privacy"
        />
      </div>

      <!-- <hr class="my-8" /> -->

      <!-- Job Overview -->

      <div class="my-8">
        <h2 class="mb-4 text-xl font-semibold">
          {{ __('Job overview') }}
        </h2>
        <JobOverview
          :job="job"
          class="mt-4 rounded-app bg-white p-8 shadow-md"
        />
      </div>
      <!-- /Job Overview -->

      <!-- <hr class="my-8" /> -->

      <!-- Job Description -->
      <div v-if="job.description" class="my-8">
        <h2 class="mb-4 text-xl font-semibold">
          {{ __('Job description') }}
        </h2>
        <div class="mt-4 rounded-app bg-white p-8 text-body shadow-md">
          <FormatedHTML :content="job.description" />
        </div>
      </div>
      <!-- /Job Description -->

      <!-- Experience needed -->
      <div v-if="renderExperienceNeededSection" class="my-8">
        <h2 class="my-4 text-xl font-semibold">
          {{ __('Experience needed') }}
        </h2>
        <div class="mt-4 rounded-app bg-white p-8 text-body shadow-md">
          <FormatedHTML :content="job.experience_needed" />
        </div>
      </div>
      <!-- /Experience needed -->

      <!-- Inclusion details -->
      <div v-if="renderInclusionDetailsSection" class="my-8">
        <h2 class="my-4 text-xl font-semibold">
          {{ __('Job Inclusivity') }}
        </h2>
        <div class="mt-4 rounded-app bg-white p-8 text-body shadow-md">
          <div v-if="job?.vulnerability?.length > 0" class="mb-4">
            <h3 class="text-lg font-semibold">
              {{ __('Vulnerabilities') }}
            </h3>
            <div>
              <span
                v-for="(vulnerability, index) in job.vulnerability"
                :key="index"
              >
                <span>
                  {{ vulnerability.name }}
                </span>
                <span
                  v-if="index !== job.vulnerability.length - 1"
                  class="px-2 text-primary"
                >
                  |
                </span>
              </span>
            </div>
          </div>
          <div v-if="job?.disability?.length > 0" class="mb-4">
            <h3 class="text-lg font-semibold">
              {{ __('Disabilities') }}
            </h3>
            <div>
              <span v-for="(disability, index) in job.disability" :key="index">
                <span>
                  {{ disability.name }}
                </span>
                <span
                  v-if="index !== job.disability.length - 1"
                  class="px-2 text-primary"
                >
                  |
                </span>
              </span>
            </div>
          </div>
          <FormatedHTML
            :content="job.inclusion_details"
            v-if="job.inclusion_details"
          />
        </div>
      </div>
      <!-- /Inclusion details -->

      <!-- <hr class="my-8" /> -->

      <!-- Skills -->
      <div class="my-8">
        <h2 class="mb-4 text-xl font-semibold">
          {{ __('Skills') }}
        </h2>
        <div class="mt-4 rounded-app bg-white p-8 shadow-md">
          <div v-if="job.skills.required.length > 0">
            <h3 class="mb-2 text-lg font-semibold">
              {{ __('Required skills') }}
            </h3>
            <SkillsView :skills="convertArray(job.skills.required)" />
          </div>

          <div v-if="job.skills.optional.length > 0">
            <hr class="my-2" />

            <h3 class="mb-2 text-lg font-semibold">
              {{ __('Optional skills') }}
            </h3>
            <SkillsView :skills="convertArray(job.skills.optional)" />
          </div>
        </div>
      </div>

      <!-- <hr class="my-8" /> -->

      <!-- Values & Perks -->
      <div class="my-8">
        <div class="md:grid md:grid-cols-2 md:gap-x-20">
          <div v-if="job.values.length > 0">
            <h2 class="mb-4 text-xl font-semibold">
              {{ __('Values') }}
            </h2>
            <ViewChips
              :data="job.values"
              class="mt-4 rounded-app bg-white p-8 shadow-md"
            />
          </div>
          <div v-if="job.perks.length > 0" class="mt-8 md:mt-0">
            <h2 class="mb-4 text-xl font-semibold">
              {{ __('Perks') }}
            </h2>
            <ViewChips
              :data="job.perks"
              class="mt-4 rounded-app bg-white p-8 shadow-md"
            />
          </div>
        </div>
      </div>

      <!-- <hr class="my-8" /> -->

      <!-- Languages & Recruitment Process -->
      <div>
        <div>
          <div v-if="job.languages.length > 0" class="my-8">
            <h2 class="mb-4 text-xl font-semibold">
              {{ __('Languages') }}
            </h2>
            <ViewLanguages
              :languages="job.languages"
              :columns="2"
              class="mt-4 rounded-app bg-white p-8 shadow-md"
            />
          </div>

          <div v-if="job.recruitment_process.length > 0" class="my-8">
            <h2 class="mb-4 text-xl font-semibold">
              {{ __('Recruitment process') }}
            </h2>
            <RecruitmentProcess
              :columns="5"
              :process="job.recruitment_process"
            />
          </div>
        </div>
      </div>
    </div>

    <transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-150 ease-in"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-2"
    >
      <div
        v-if="showAiAnalyzeTab"
        class="flex transition-all duration-300 ease-in-out"
        :class="{
          'w-full sm:w-[35%]': detailPage && (aiAnalyze || loadingAiAnalyze),
          'w-full': !(detailPage && (aiAnalyze || loadingAiAnalyze))
        }"
      >
        <div
          :job="job"
          class="mt-4 flex w-full flex-col rounded-app bg-white py-8 shadow-md"
          :class="seeMoreAiAnalyzeMobile ? 'h-auto' : 'h-[350px] sm:h-[725px]'"
        >
          <div
            class="mx-8 flex items-center gap-x-2 border-b border-zinc-200 pb-4"
          >
            <svg
              class="rounded-md bg-[#E7F8F2] p-1"
              :class="loadingAiAnalyze ? 'animate-pulse' : ''"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M4.89205 10.092L7.04205 13.542C7.22538 13.8254 7.47955 13.942 7.80455 13.892C8.12955 13.842 8.33371 13.6504 8.41705 13.317L9.11705 10.517L15.942 17.342C16.1254 17.5254 16.3545 17.6212 16.6295 17.6295C16.9045 17.6379 17.142 17.542 17.342 17.342C17.5254 17.1587 17.617 16.9254 17.617 16.642C17.617 16.3587 17.5254 16.1254 17.342 15.942L10.517 9.09205L13.317 8.39205C13.6504 8.30871 13.842 8.10455 13.892 7.77955C13.942 7.45455 13.8254 7.20038 13.542 7.01705L10.092 4.89205L10.392 0.817046C10.4254 0.483712 10.292 0.242045 9.99205 0.0920455C9.69205 -0.0579545 9.41705 -0.0246212 9.16705 0.192045L6.04205 2.81705L2.26705 1.29205C1.95038 1.15871 1.67538 1.20871 1.44205 1.44205C1.20871 1.67538 1.15871 1.95038 1.29205 2.26705L2.81705 6.04205L0.192046 9.14205C-0.024621 9.39205 -0.0579547 9.66705 0.0920453 9.96705C0.242045 10.267 0.483712 10.4004 0.817046 10.367L4.89205 10.092ZM17.692 3.39205C17.792 3.29205 17.842 3.17538 17.842 3.04205C17.842 2.90871 17.792 2.79205 17.692 2.69205L16.392 1.39205C16.292 1.29205 16.1754 1.24205 16.042 1.24205C15.9087 1.24205 15.792 1.29205 15.692 1.39205L14.392 2.69205C14.292 2.79205 14.242 2.90871 14.242 3.04205C14.242 3.17538 14.292 3.29205 14.392 3.39205L15.692 4.69205C15.792 4.79205 15.9087 4.84205 16.042 4.84205C16.1754 4.84205 16.292 4.79205 16.392 4.69205L17.692 3.39205ZM3.39205 17.692L4.69205 16.392C4.79205 16.292 4.84205 16.1754 4.84205 16.042C4.84205 15.9087 4.79205 15.792 4.69205 15.692L3.39205 14.392C3.29205 14.292 3.17538 14.242 3.04205 14.242C2.90871 14.242 2.79205 14.292 2.69205 14.392L1.39205 15.692C1.29205 15.792 1.24204 15.9087 1.24204 16.042C1.24204 16.1754 1.29205 16.292 1.39205 16.392L2.69205 17.692C2.79205 17.792 2.90871 17.842 3.04205 17.842C3.17538 17.842 3.29205 17.792 3.39205 17.692Z"
                fill="#27272A"
              />
            </svg>
            <h2 class="text-lg font-medium">
              {{ __('Analyze with AI') }}
            </h2>
          </div>

          <div class="h-full sm:hidden">
            <div
              v-if="loadingAiAnalyze"
              class="flex h-full w-full flex-col items-center justify-center gap-y-1"
            >
              <IconV2
                class="animate-spin !text-3xl text-zinc-600"
                name="progress_activity"
              />
              <span class="font-medium text-zinc-600">
                {{ __('Analyzing...') }}
              </span>
            </div>

            <div
              v-if="aiAnalyze !== null && !loadingAiAnalyze"
              v-html="aiAnalyze"
              :class="seeMoreAiAnalyzeMobile ? 'max-h-none' : 'max-h-[200px]'"
              class="overflow-hidden px-8 py-4 text-sm transition-all duration-300"
            ></div>
          </div>

          <!-- Desktop: scrollable -->
          <div
            class="scrollbar-thin hidden h-full w-full overflow-y-auto px-8 py-4 sm:block"
          >
            <transition
              enter-active-class="transition-opacity duration-[2000ms] ease-out"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
            >
              <div
                :key="aiAnalyze"
                v-html="aiAnalyze"
                v-if="aiAnalyze !== null && !loadingAiAnalyze"
                class="h-[725px] text-sm"
              ></div>
            </transition>

            <div
              v-if="loadingAiAnalyze"
              class="flex h-full w-full flex-col items-center justify-center gap-y-1"
            >
              <IconV2
                class="animate-spin !text-3xl text-zinc-600"
                name="progress_activity"
              />
              <span class="font-medium text-zinc-600">
                {{ __('Analyzing...') }}
              </span>
            </div>
          </div>

          <button
            v-if="aiAnalyze !== null && !loadingAiAnalyze"
            @click="seeMoreAiAnalyzeMobile = !seeMoreAiAnalyzeMobile"
            class="mt-auto flex h-10 w-full items-center justify-center font-medium text-primary sm:hidden"
          >
            {{ seeMoreAiAnalyzeMobile ? 'See less' : 'See more' }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { computed, onMounted, reactive, ref, watch } from 'vue';

  import {
    Button,
    FormatedHTML,
    Icon,
    Textarea
  } from '../../../../components/global';
  import { __, scrollToBottom } from '../../../../helpers';
  import {
    JobSafetyHover,
    SkillsView,
    ViewChips,
    ViewLanguages
  } from '../components';
  import {
    ApplyWithoutAccountForm,
    JobOverview,
    JobQuickEdit,
    RecruitmentProcess
  } from '../components';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import IconV2 from '@/components/block/icon/icon.vue';
  import VerifyYourAccountModal from "@/pages/candidate/jobs/components/VerifyYourAccountModal.vue";
  import {Dialog, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
  import {JobSingle} from "@/pages/candidate/jobs/partials/index";

  const props = defineProps({
    job: Object,
    user_type: {
      type: String,
      required: true
    },
    user_profile: {
      type: String,
      required: false
    },
    config_general_single_company: {
      type: Number,
      required: true
    },
    options: {
      type: Object
    },
    target: {
      type: String,
      default: ''
    },
    detailPage: {
      type: Boolean,
      default: true
    },
    reportMisconductOn: {
      type: Number,
      required: true
    },
    terms: {
      type: String,
      required: false
    },
    privacy: {
      type: String,
      required: false
    }
  });

  onMounted(() => {
    if (sessionStorage.getItem('triggerAiAnalyze')) {
      setTimeout(() => {
        analyzeJobWithAI();
      }, 500);
    }
    sessionStorage.removeItem('triggerAiAnalyze');
  });
  const job = reactive(props.job);

  const detailPage = ref(props.detailPage);

  const toast = useToast();

  const aiAnalyze = ref(null);
  const loadingAiAnalyze = ref(false);
  const modalData = ref(null);
  const quickApplySuccessOpen = ref(false);
  const closeSuccessModal = () => {
    quickApplySuccessOpen.value = false;
  };
  // Fix for the future field added on a skill
  const convertArray = (array) => {
    let newArray = [];
    array.forEach((element) => {
      newArray.push(element.skill);
    });
    return newArray;
  };

  const loadingApply = ref(false);

  const renderExperienceNeededSection = computed(() => {
    return (
      props.job.experience_needed &&
      props.job.experience_needed.trim() !== '<p></p>'
    );
  });

  const renderInclusionDetailsSection = computed(() => {
    return (
      (props.job.inclusion_details &&
        props.job.inclusion_details.trim() !== '<p></p>') ||
      props.job.vulnerability?.length ||
      props.job.disability?.length
    );
  });

  const handleApply = () => {
    if (job.conditions.is_applied) {
      toast.error(__('You have already applied to this job.'));
      return;
    }

    loadingApply.value = true;

    axios
      .post(
        route('redesign.jobs.apply', {
          company: job.company.slug,
          job: job.slug
        })
      )
      .then((response) => {
        job.conditions.is_applied = !job.conditions.is_applied;
        toast.success(response.data.message);
        loadingApply.value = false;
      })
      .catch((error) => {
        toast.error(error.response.data.message || __('An error occurred.'));
        loadingApply.value = false;
      });
  };

  const handleSave = () => {
    axios
      .post(
        route('redesign.jobs.save', {
          company: job.company.slug,
          job: job.slug
        }),
        { saved: !job.conditions.is_saved }
      )
      .then((response) => {
        job.conditions.is_saved = !job.conditions.is_saved;
        toast.success(response.data.message);

        emit('save', job);
      })
      .catch((error) => {
        toast.error(error.response.data.message || __('An error occurred.'));
      });
  };

  const showApplyForm = ref(false);

  const emit = defineEmits([
    'save',
    'accept_invitation',
    'decline_invitation',
    'apply-success'
  ]);

  const handleApplySuccess = (data) => {
    quickApplySuccessOpen.value = true;
    modalData.value = data;
    // emit('apply-success', data);
  };
  // Reject Reason
  const reject_reason = ref('');
  const show_reject_reason = ref(false);
  const loading_handle_invitation = ref(false);

  const handleAcceptInvitation = () => {
    loading_handle_invitation.value = true;

    axios
      .post(
        route('redesign.jobs.accept-invitation', {
          company: job.company.slug,
          job: job.slug
        })
      )
      .then((response) => {
        // is_apllied true
        job.conditions.is_applied = true;

        // is_invited false
        emit('accept_invitation', job);
        toast.success(response.data.message);

        loading_handle_invitation.value = false;
      })
      .catch((error) => {
        toast.error(error.response.data.message || __('An error occurred.'));

        loading_handle_invitation.value = false;
      });
  };

  const handleDeclineInvitation = () => {
    loading_handle_invitation.value = true;

    axios
      .post(
        route('redesign.jobs.reject-invitation', {
          company: job.company.slug,
          job: job.slug
        })
      )
      .then((response) => {
        job.conditions.is_invited = false;
        job.conditions.can_apply = false;

        emit('decline_invitation', job);

        show_reject_reason.value = true;
        toast.success(response.data.message);

        loading_handle_invitation.value = false;
      })
      .catch((error) => {
        toast.error(error.response.data.message || __('An error occurred.'));
        // loadingApply.value = false;

        loading_handle_invitation.value = false;
      });
  };

  const handleRejectReason = () => {
    // check if the reject_reason is not empty
    if (reject_reason.value === '') {
      toast.error(__('Please provide a reason for rejecting this invitation'));
      return;
    }

    axios
      .post(
        route('redesign.invitations.reject-reason', {
          invitation: job.conditions.invitation_id
        }),
        {
          reject_reason: reject_reason.value
        }
      )
      .then((response) => {
        reject_reason.value = '';
        show_reject_reason.value = false;
        // TODO: change state

        toast.success(response.data.message);
      })
      .catch((error) => {
        toast.error(error.response.data.message || __('An error occurred.'));
        // loadingApply.value = false;
      });
  };

  const handleSkipReason = () => {
    reject_reason.value = '';
    show_reject_reason.value = false;
  };

  const analyzeJobWithAI = async () => {
    loadingAiAnalyze.value = true;
    try {
      const response = await axios.post(
        route('redesign.jobs.ai-analyze', {
          job: props.job.slug,
          company: job.company.slug
        })
      );

      if (response.data && response.data.message) {
        aiAnalyze.value = response.data.message;
      }
    } catch (error) {
      toast.error(error.response.data.message || __('An error occurred.'));
    } finally {
      setTimeout(() => {
        loadingAiAnalyze.value = false;
      }, 3000);
    }
  };

  const isAiAnalyzeTabVisible = computed(() => {
    return (
      job?.show_ai_candidate_job_analysis &&
      detailPage.value &&
      (aiAnalyze.value !== null || loadingAiAnalyze.value)
    );
  });

  const showAiAnalyzeTab = ref(false);

  watch(isAiAnalyzeTabVisible, (newValue) => {
    if (newValue) {
      setTimeout(() => {
        showAiAnalyzeTab.value = true;
        if (window.innerWidth < 768) scrollToBottom();
      }, 150);
    } else {
      showAiAnalyzeTab.value = false;
    }
  });

  const seeMoreAiAnalyzeMobile = ref(false);

  const goToDetailPage = (job_url) => {
    sessionStorage.setItem('triggerAiAnalyze', 'true');
    // window.location.href = job_url;
    window.open(job_url, '_blank');
  };
</script>
